import {Injectable} from '@angular/core';
import {Observable, Subject, BehaviorSubject} from 'rxjs';
import {Principal} from '../../models/principal/principal';
import {Authority} from '../../models/principal/authority';
import {switchMap, tap} from 'rxjs/operators';
import {FabricService} from 'src/app/services/fabric/fabric.service';
import {Permission, Permissions} from 'src/app/models/principal/permission';
import {PERMISSIONS_MAP} from 'src/app/models/principal/permissions-map';
import { Router } from '@angular/router';
import { RouterSegments } from 'src/app/models/router-segments';
import { JwtHelperService } from '@auth0/angular-jwt';
import * as CryptoJS from 'crypto-js';
import { translate } from '@jsverse/transloco';
import { CustomToastrService } from '../util/toastr.service';
import { PermissionValues } from 'src/app/models/permission-values';


declare const kony: any;

const SERVICE_NAME = 'mgmt-logout-resources';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private client: any;

  private _principal: Principal;

  get principal(): Principal {
    return this._principal;
  }

  set principal(value: Principal) {
    this._principal = value;
  }

  encryptedBase64Key: string = "ZmFicmljc2VjcmV0a2V5cw==";

  assignedPermissions: any;
  authorityGroups: any;
  scope: any;
  assignedFeaturePermissions: any;

  private permissionList = new BehaviorSubject('');
  getPermissionList = this.permissionList.asObservable();

  private featurePermissionList = new BehaviorSubject('');
  getFeaturePermissionList = this.featurePermissionList.asObservable();

  constructor(private fabricService: FabricService, private router: Router, private toastrService: CustomToastrService) {
  }

  tenantPermissionList(permission: any) {
    this.permissionList.next(permission);
  }

  tenantFeaturePermissionList(permission: any) {
    this.featurePermissionList.next(permission);
  }

  logout(): void {
    this.keycloakLogout().pipe().subscribe(
      response => {
        this.fabricService.logout();
        window.location.href = `${window.location.origin}/${this.principal.provider}`;
      }
    );
  }



  keycloakLogout(): Observable<any> {
    const provider = this.principal.provider;
    let logoutURL;
    if(provider === 'temenosinternal') {
    logoutURL = `https://keycloak.internal.shs.saas.temenos.cloud/realms/temenosinternal/protocol/openid-connect/logout`;
    } else {
    logoutURL = `https://keycloak.shs.saas.temenos.cloud/realms/temenos.com/protocol/openid-connect/logout`;
    }

    return this.fabricService.invokeOperation<any>(SERVICE_NAME, 'logout', { provider, logoutURL },
      { 'Content-Type': 'application/x-www-form-urlencoded' });

  }

  login(): any {
    return this.fabricService.login();
  }

  loadPrincipal(): Observable<any> {
    return this.fabricService.invokeOperation<any>('mgmt-application-resources', 'get_userinfo')
      .pipe(switchMap((principal) => {
      this.principal = principal;
      if(!principal.authorityGroups) {
        this.router.navigate(['/', this.principal.provider, RouterSegments.UNAUTHORIZED]);
      }
        if (principal?.code) {
          const parsedBase64Key = CryptoJS.enc.Base64.parse(this.encryptedBase64Key);
          const encryptedCipherText = principal?.code;
          const decryptedData = CryptoJS.AES.decrypt(encryptedCipherText, parsedBase64Key, {
            mode: CryptoJS.mode.ECB,
            padding: CryptoJS.pad.Pkcs7
          });
          const decryptedText = decryptedData.toString(CryptoJS.enc.Utf8);

          const jwtService = new JwtHelperService();
          const decode = jwtService.decodeToken(decryptedText);
          const authorityGroups = decode?.groups;
          this.authorityGroups = authorityGroups;
          let validItems = authorityGroups.filter((tokenItems) => {
            return principal?.authorityGroups.some((authItems) => { return tokenItems === authItems;
          })});

          const tenantId = window.location.pathname?.split('/')[3];
          if (tenantId) {
            this.fabricService.invokeOperation<any>('mgmt-feature-resources', 'get_mgmt_tenants_tenantid_features', {tenantId})
            .pipe()
            .subscribe(features => {
              this.assignedFeaturePermissions = features;
            },(error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
            });
          }


          if(authorityGroups?.length > 0 && validItems) {
            this.principal = principal;
            let scope = '/global';
            const tenant = window.location.pathname?.split('/')[3];
            if (window.location.pathname?.split('/')[2] === 'tenant' && tenant) {
              scope = PermissionValues.TENANT + tenant;
            }
            this.scope = scope;
            return this.fabricService.invokeOperation<any>('mgmt-permission-resources', 'get_mgmt_authz_assigned-permissions', {scope})
            .pipe(tap((response) => {
              this.assignedPermissions = response.permissions;
              if(this.assignedPermissions.length === 0) {
                this.router.navigate(['/', this.principal.provider, RouterSegments.UNAUTHORIZED]);
              }
            },
            (error) => {
              this.router.navigate(['/', this.principal.provider, RouterSegments.PERMISSION_ERROR]);
            }));
          } else {
            this.principal = principal;
            this.router.navigate(['/', this.principal.provider, RouterSegments.UNAUTHORIZED]);
            setTimeout(() => {
              this.logout();
            }, 2000);
          }
        }
      }
    ));
  }

  loadRefresh(): Observable<any> {
    return this.fabricService.invokeOperation<any>('mgmt-application-resources', 'get_userinfo')
    .pipe(tap((response) => {
      this.principal = response;
      })
      )
    }

  hasPermission(permission: Permission): boolean {
    const authoritiesForPermission = PERMISSIONS_MAP[permission];
    return this.principal.authorities.some(authority => authoritiesForPermission?.includes(authority));
  }

  hasAPIPermission(permission: any): boolean {
    if (permission.attribute) {
      return this.assignedPermissions.find(item => item.action === permission.action && item.attribute === permission.attribute && item.attributeValue === permission.attributeValue) ? true : false;
    } else {
      return this.assignedPermissions.find(item => item.action === permission.action && item.hasOwnProperty('attribute') === false && item.hasOwnProperty('attributeValue') === false) ? true : false;
    }
  }

  hasAuthority(givenAuthority: Authority): boolean {
    return this.principal.authorities.includes(givenAuthority);
  }

  hasFeaturePermission(permission: any): boolean {
    if (permission.attribute) {
      const feature = this.assignedFeaturePermissions?.find(item => item.name === permission.name && item.enabled === 'true');
      return feature && feature?.dataZones?.find(item => item.dataZone === permission.attributeValue && item.enabled === 'true') ? true : false;
    } else {
      return this.assignedFeaturePermissions?.find(item => item.name === permission.name && item.enabled === 'true') ? true : false;
    }
  }

}
